import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import './FilterPanel.scss';
import ArrTextField from '../../../common/arr-text-field/ArrTextField';
import ArrDatePicker from '../../../common/arr-date-picker/ArrDatePicker';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrTooltipWrapper from '../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ArrToggle} from '../../../common/arr-toggle/ArrToggle';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from '../../../../assets/images/info-icon.svg';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import {useUserRole} from '../../../../hooks/users/useUsers';
import {IRole} from '../../../../api/types/user.type';
import {chekIfValidDate} from '../../../../utils/utils';

type FilterPanelProps = {
  initialNameOrEmail?: string;
  initialRoleId: ISelectItem;
  initialDate?: string;
  initialIsActive?: boolean;

  onApplyFilters: (nameOrEmail: string, roleId: number, date: string) => void;

  onIsActiveChange?: (isActive: boolean) => void;
};

const FilterPanel: React.FC<FilterPanelProps> = ({
  initialNameOrEmail = '',
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  initialRoleId,
  initialDate = '',
  initialIsActive = false,
  onApplyFilters,
  onIsActiveChange,
}) => {
  const [nameOrEmail, setNameOrEmail] = useState(initialNameOrEmail);
  const [selectedRoleId, setSelectedRoleId] = useState<IRole | null>(null);
  const [dateValue, setDateValue] = useState(initialDate);
  const [isActive, setIsActive] = useState(initialIsActive);
  const [isApplyButtonDisabled, setIsApplyButtonDisabled] = useState(false);
  const {data: roles} = useUserRole() as any;
  const [isDateError, setIsDateError] = useState(false);

  const handleApply = () => {
    onApplyFilters(
      nameOrEmail,
      selectedRoleId?.key as unknown as number,
      dateValue
    );
  };

  const handleToggleChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsActive(checked);
    onIsActiveChange?.(checked);
  };

  const handleOnDateChange = (newDateValue: any) => {
    setDateValue(newDateValue as string);
    if (newDateValue.trim() === '') {
      setIsApplyButtonDisabled(false);
      setIsDateError(false);
      return;
    }

    const isValid = chekIfValidDate(newDateValue);
    setIsApplyButtonDisabled(!isValid);
    setIsDateError(!isValid);
  };

  const handleKeyEnter = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      handleApply();
    }
  };

  return (
    <Box className="view-user-filter-panel">
      <Box className="view-user-filter-panel-controls">
        <div
          className="view-user-item-flex"
          data-testid={`filter-panel-view-users-div`}
        >
          <ArrTextField
            state="enabled"
            sizeVariant="medium"
            slotProps={{inputLabel: {shrink: false}}}
            placeholder={t('userManagement.manageUsers.nameOrEmail')}
            aria-label={t('userManagement.manageUsers.nameOrEmail')}
            value={nameOrEmail}
            onChange={(e) => setNameOrEmail(e.target.value)}
            onKeyDown={handleKeyEnter}
            dataTestId="filter-panel-name-or-email-input"
            type="clear"
          />
        </div>
        <div
          className="view-user-item-flex"
          data-testid={`filter-panel-div-dropdown`}
        >
          <ArrSingleSelectDropdown
            dropdownLabel={t('userManagement.manageUsers.selectRole')}
            dropdownItems={roles?.applicableRoles ?? []}
            selectedValues={selectedRoleId as any}
            onSelectionChange={(value) =>
              setSelectedRoleId(value as unknown as IRole)
            }
            sizeVariant="medium"
            dropdownItemKey="key"
            dropdownItemDisplay="value"
            dataTestId="asd-filter-panel-select-role"
          />
        </div>
        <div
          className="view-user-item-flex"
          data-testid={`filter-panel-div-date-picker`}
        >
          <ArrDatePicker
            type="date-picker"
            placeholder={t('userManagement.manageUsers.dateLabel')}
            dataTestId={t('userManagement.manageUsers.dateLabel')}
            isError={isDateError}
            showToday={true}
            inputClass="view-user-filter-datepicker"
            defaultError={false}
            initialStartDate={dateValue}
            onChange={handleOnDateChange}
            onClearClick={() => setDateValue('')}
          />
        </div>
        <ArrButton
          aria-label={t('userManagement.manageUsers.applyFilters')}
          variant="secondary"
          size="medium"
          onClick={handleApply}
          disabled={isApplyButtonDisabled}
          dataTestId="apply-filter-btn"
        >
          {t('userManagement.manageUsers.applyFilters')}
        </ArrButton>
      </Box>
      <Box display="flex" alignItems="center" gap={1}>
        <ArrToggle
          disabled={false}
          checked={isActive}
          onChange={handleToggleChange}
          dataTestId="filter-panel-toggle"
        />
        <Typography
          className="typo-text body3_size"
          data-testid={`filter-panel-inactive-users`}
        >
          {t('userManagement.manageUsers.inactiveUsers')}
        </Typography>
        <ArrTooltipWrapper
          trigger={
            <InfoIcon
              className="view-user-info-icon"
              data-testid="toggle-info-tooltip"
            />
          }
        >
          {t('userManagement.manageUsers.inactiveUsersShortInfo')}
        </ArrTooltipWrapper>
      </Box>
    </Box>
  );
};

export default FilterPanel;
